<template>
  <avs-card>
    <template v-slot:title>
      <v-toolbar
        class="mt-4 custom-toolbar"
        elevation="0"
      >
        <v-text-field
          clearable
          hide-details
          prepend-inner-icon="mdi-magnify"
          :label="$t('commons.search')"
          v-model="search"
        />
        <slot
          name="actions"
          v-bind:dataset="dataset"
        />
      </v-toolbar>
    </template>
    <template v-slot:content>
      <v-data-table
        class="ma-10"
        disable-pagination
        :no-data-text="$t('messages.information.no_data')"
        :headers="headers"
        hide-default-footer
        :items="dataset"
        :loading="loading"
        loading-text="Loading inspections"
        no-results-text="$t('messages.information.no_data')"
        :search="search"
        @dblclick:row="($event, { item }) => toEdit(item)"
      >
        <template #item.start_date="{ item }">
          {{ toDatetime(item.start_date) }}
        </template>
        <template #item.end_date="{ item }">
          {{ toDatetime(item.end_date) }}
        </template>
        <template v-slot:item.status="{ item }">
          <component :is="handleStatusComponent(item)" />
        </template>
        <template v-slot:item.actions="{ item }">
          <div class="d-flex justify-end">
            <avs-webcamButton
              :disabled="isMettingDisabled(item)"
              :to="getLinkToMeeting(item)"
              :spantext="
                $t('actions.join', { entity: $tc('categories.inspection.name', 1).toLowerCase() })
              "
            />
            <avs-button
              fab
              iconI="mdi-export"
              icon
              label
              small
              color="secondary"
              :spantext="
                $t('actions.export', { entity: $tc('categories.inspection.name', 1).toLowerCase() })
              "
              @click="exportValues(item)"
            />

            <avs-duplicateButton
              @click.stop="$event => duplicateInspection(item)"
              :spantext="
                $t('actions.duplicate', {
                  entity: $tc('categories.inspection.name', 1).toLowerCase()
                })
              "
            />

            <avs-editButton
              :to="getToEditLink(item)"
              :spantext="
                $t('actions.edit', { entity: $tc('categories.inspection.name', 1).toLowerCase() })
              "
            />
            <avs-deleteButton
              @click="handleOpenDeleteDialog(item)"
              :spantext="
                $t('actions.delete', { entity: $tc('categories.inspection.name', 1).toLowerCase() })
              "
            />
          </div>
        </template>
      </v-data-table>

      <!-- Ajoutez ici votre footer personnalisé si nécessaire -->
      <!-- <div class="d-flex justify-end ma-2">
        <v-pagination
          v-model="page"
          :length="pageCount"
          circle
        ></v-pagination>
      </div> -->

      <avs-deleteDialog
        v-model="deleteDialogIsOpen"
        @cancel="handleCancel"
        @delete="handleDelete"
        resource="inspection"
      />
    </template>
  </avs-card>
</template>

<script>
import { InspectionService } from "@/services"
import { DocumentService, TemplateService } from "@/services/documents"
import { toDatetime } from "@/utils/datetime"
import { injectValues } from "@/utils/datamodels"
import { handleExportCSV, fmtTemplate, fmtValues } from "@/utils/download"
import { Closed, Ongoing, Overdue, Pending } from "@/components/generics/status"
import { STATE_ENUM } from "@/utils/consts"
import { dateToString, now, FMTS } from "@/utils/datetime"
import { slugify } from "@/utils/string"

export default {
  name: "InspectionsTable",
  components: { Closed, Ongoing, Overdue, Pending },
  computed: {
    project() {
      return this.$store.state.app.project
    }
  },
  data() {
    return {
      dataset: [],
      deleteDialogIsOpen: false,
      document: null,
      headers: [
        { text: this.$t("commons.name"), value: "name", class: "min-width-200" },
        { text: this.$t("commons.date.start_date"), value: "start_date", class: "min-width-150" },
        { text: this.$t("commons.date.end_date"), value: "end_date", class: "min-width-150" },
        { text: this.$t("commons.status.name"), align: "center", value: "status" },
        {
          text: this.$t("commons.actions"),
          sortable: false,
          value: "actions",
          align: "right",
          width: 200
        }
      ],
      loading: false,
      search: "",
      selectedItem: null,
      template: null,
      values: null,
      duplicate_document: null,
      duplicate_inspection: null,
      duplicate_values: null
      // page: 1,
      // pageCount: 0
    }
  },
  methods: {
    toDatetime,
    getDataset() {
      this.loading = true
      InspectionService.get(this, this.project.id)
        .then(response => {
          this.dataset = response.results
        })
        .catch(err => {
          if (err.response && err.response.status === 403) {
            this.$store.commit("app/message", {
              msg: this.$t("messages.error.permission_denied"),
              color: "error"
            })
          }
        })
        .finally(() => {
          this.loading = false
        })
    },
    getLinkToMeeting(item) {
      return {
        name: "InspectionMeeting",
        params: { id: item.id, uuid: item.uuid }
      }
    },
    getToEditLink(item) {
      return {
        name: "Inspection",
        params: { id: item.id }
      }
    },
    handleOpenDeleteDialog(item) {
      this.deleteDialogIsOpen = true
      this.selectedItem = item
    },
    handleCancel() {
      this.deleteDialogIsOpen = false
    },
    handleDelete(item) {
      InspectionService.remove(this, this.selectedItem.id).then(r => {
        this.dataset = this.dataset.filter(nc => nc.id !== this.selectedItem.id)
        this.$store.commit("app/message", {
          msg: this.$t("messages.success.deleted", {
            entity: this.$tc("categories.inspection.name", 1)
          }),
          color: "success"
        })
      })
      this.deleteDialogIsOpen = false
    },
    handleStatusComponent(task) {
      return task.status
    },
    isMettingDisabled(item) {
      return item.status === "Closed"
    },
    async exportValues(item) {
      const format = "csv"
      InspectionService.document
        .get(this, item.id)
        .then(async document => {
          const template = await TemplateService.details.get(document.template.id)
          const valuesD = await DocumentService.values.getAll(document.id)
          this.document = injectValues(template, valuesD)
          let templateFMT = fmtTemplate(this.document)
          let valuesFMT = fmtValues(this.document)
          handleExportCSV([templateFMT, valuesFMT], format)
        })
        .catch(message => {
          this.$store.commit("app/message", { msg: message, color: "warning" })
        })
    },
    toEdit(item) {
      this.$router.push(this.getToEditLink(item))
    },
    async duplicateInspection(p) {
      InspectionService.get(this, null, p.id)
        .then(response => {
          // Override some keys
          let date = dateToString(now(), FMTS.FILE)
          response.name = `${response.name}-${date}`
          response.slug = slugify(response.name)
          response.step = null
          response.inspector = null
          // On Créé l'inspection
          InspectionService.create(this, this.project.id, response)
            .then(res => {
              this.duplicate_inspection = res
              // this.getDataset()

              //On passe aux documents
              //recuperation du document
              InspectionService.document
                .get(this, p.id)
                .then(resp => {
                  resp.state = STATE_ENUM.EDITABLE
                  // On relie le document a la nouvelle inspection
                  InspectionService.document.post(this.duplicate_inspection.id, resp).then(res => {
                    this.duplicate_document = res

                    // On recupère les valeurs des documents
                    DocumentService.values.getAll(resp.id).then(result => {
                      this.duplicate_values = result
                      // On ajoute les valeurs aux documennts
                      DocumentService.values
                        .put(this.duplicate_document.id, this.duplicate_values)
                        .then(resultt => {
                          this.$store.commit("app/message", {
                            msg: this.$t("messages.success.duplicated", {
                              entity: this.$tc("categories.inspection.name", 1)
                            }),
                            color: "success"
                          })
                        })
                        .catch(error => {
                          this.$emit("error", error)
                        })
                      this.getDataset()
                    })
                  })
                })
                .catch(error => {
                  this.$store.commit("app/message", {
                    msg: this.$t("messages.success.duplicated", {
                      entity: this.$tc("categories.inspection.name", 1)
                    }),
                    color: "success"
                  })
                  this.getDataset()
                })
            })
            .catch(error => {
              this.$emit("error", error)
            })

          // DocumentService.values.getAll(this.document.id).then(res => {
          //   this.duplicate_document = res
          //   InspectionService.document.post(this.inspection.id, document)
          // })
        })
        .catch(e => {
          if (e.status) {
            this.$store.commit("app/message", {
              msg: this.$t("messages.error.not_allowed_duplicated"),
              color: "error"
            })
          }
        })
    }
  },
  mounted() {
    this.getDataset()
  }
}
</script>

<style scoped>
.custom-toolbar >>> .v-toolbar__content {
  gap: 1rem;
}
</style>
